import { useRef, useState } from "react";
import clsx from "clsx";
import { useHoverCapable } from "@/hooks/useHoverCapable";
import { PROJECT_WORKFLOW_STATUS_LABELS } from "@/lib/projects";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";
import { assignmentBarLabel } from "./displayLabels";
import { BookedAssignmentTooltip } from "./BookedAssignmentTooltip";
import type { BarGeom } from "./AssignmentBar";
import {
  bookedSpanIsPaid,
  bookedWorkflowEdgeClass,
  bookedWorkflowFillClass,
  type BookedAssignmentSpan,
  type BookedColorMode,
} from "./bookedAssignments";

export function BookedAssignmentBar({
  span,
  geom,
  dayWidth,
  barHeight,
  barTop,
  colorMode,
}: {
  span: BookedAssignmentSpan;
  geom: BarGeom;
  dayWidth: number;
  barHeight: number;
  barTop: number;
  colorMode: BookedColorMode;
}) {
  const { open: openProjectOverview } = useProjectOverview();
  const containerRef = useRef<HTMLDivElement>(null);
  const hoverCapable = useHoverCapable();
  const [hovered, setHovered] = useState(false);
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);

  const inset = geom.span === 1 ? 1 : 2;
  const width = geom.span * dayWidth - inset * 2;
  const left = geom.startIdx * dayWidth + inset;
  const label = assignmentBarLabel(span.title, geom.span, width);
  const statusLabel = PROJECT_WORKFLOW_STATUS_LABELS[span.workflowStatus];
  const secondaryLabel = span.workflowStatusSecondary
    ? PROJECT_WORKFLOW_STATUS_LABELS[span.workflowStatusSecondary]
    : null;
  const paid = bookedSpanIsPaid(span);
  const showStatusColor = colorMode === "status";

  function handleMouseEnter(e: React.MouseEvent) {
    if (!hoverCapable) return;
    setHovered(true);
    setPointer({ x: e.clientX, y: e.clientY });
  }

  function handleMouseMove(e: React.MouseEvent) {
    if (!hoverCapable || !hovered) return;
    setPointer({ x: e.clientX, y: e.clientY });
  }

  function handleMouseLeave() {
    if (!hoverCapable) return;
    setHovered(false);
    setPointer(null);
  }

  return (
    <>
      <div
        ref={containerRef}
        className="mp-bar-container mp-bar-container-booked"
        style={{ left, width, top: barTop, height: barHeight }}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <button
          type="button"
          className={clsx(
            "mp-bar",
            "mp-bar-booked",
            showStatusColor && bookedWorkflowFillClass(span.workflowStatus),
            showStatusColor &&
              span.workflowStatusSecondary &&
              bookedWorkflowEdgeClass(span.workflowStatusSecondary),
            !showStatusColor && (span.deviates ? "mp-bar-booked-plan-deviates" : "mp-bar-booked-plan"),
            paid && "mp-bar-booked-paid",
            geom.clipLeft && "mp-bar-clip-left",
            geom.clipRight && "mp-bar-clip-right",
            geom.span === 1 && "mp-bar-single-day",
            label !== span.title && "mp-bar-abbrev",
          )}
          aria-label={
            [
              span.title,
              showStatusColor ? statusLabel : span.deviates ? "Abweichung von der Planung" : "Gebucht laut Planung",
              secondaryLabel,
              paid ? "vollständig bezahlt" : null,
            ]
              .filter(Boolean)
              .join(", ")
          }
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setHovered(false);
            openProjectOverview(span.auftragId);
          }}
        >
          <span className="mp-bar-label">{label}</span>
          {paid ? (
            <span className="mp-bar-paid-mark" title="Vollständig bezahlt" aria-hidden>
              ✓
            </span>
          ) : null}
        </button>
      </div>
      <BookedAssignmentTooltip
        span={span}
        anchorRef={containerRef}
        pointerX={pointer?.x}
        visible={hoverCapable && hovered}
        colorMode={colorMode}
      />
    </>
  );
}
